import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';

type Status = 'verifying' | 'success' | 'error';

const MagicLinkVerifyPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithSession } = useAuth();
  const [status, setStatus] = useState<Status>('verifying');
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const ranRef = useRef(false);

  const token = params.get('token');

  useEffect(() => {
    if (ranRef.current) return; // guard against StrictMode double-invoke
    ranRef.current = true;

    if (!token) {
      setStatus('error');
      setErrorCode('missing');
      setErrorMsg('Kein Token in der URL gefunden.');
      return;
    }

    (async () => {
      try {
        const resp = await apiService.verifyMagicLink(token);
        if (resp.data?.success && resp.data.token && resp.data.user) {
          loginWithSession(resp.data.token, resp.data.user);
          setStatus('success');
          setTimeout(() => navigate('/admin'), 800);
        } else {
          setStatus('error');
          setErrorCode(resp.data?.code || 'unknown');
          setErrorMsg(resp.data?.message || 'Login-Link konnte nicht verifiziert werden.');
        }
      } catch (e: any) {
        const data = e?.response?.data;
        setStatus('error');
        setErrorCode(data?.code || 'unknown');
        setErrorMsg(data?.message || 'Login-Link konnte nicht verifiziert werden.');
      }
    })();
  }, [token, loginWithSession, navigate]);

  return (
    <div data-admin-shell data-admin-theme="mediterranean" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-brand-accent/10 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Brand header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-primary to-brand-primary-hover shadow-lg ring-2 ring-brand-accent/30 ring-offset-2 ring-offset-gray-100">
            <span className="text-white text-2xl">🦷</span>
          </div>
          <h2 className="mt-5 text-3xl font-extrabold text-gray-900 tracking-tight">SeaDent</h2>
          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-brand-accent">
            Dental Practice Management
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md ring-1 ring-black/5 p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-brand-accent/15 flex items-center justify-center mb-4">
                <div className="h-8 w-8 border-4 border-brand-accent/30 border-t-brand-primary rounded-full animate-spin" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Login-Link wird geprüft…</h3>
              <p className="text-sm text-gray-600 mt-2">Einen Moment bitte.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-brand-accent/20 flex items-center justify-center mb-4 text-3xl">
                ✅
              </div>
              <h3 className="text-lg font-bold text-gray-900">Eingeloggt</h3>
              <p className="text-sm text-gray-600 mt-2">Du wirst gleich weitergeleitet…</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-red-100 flex items-center justify-center mb-4 text-3xl">
                {errorCode === 'expired' ? '⏰' : '⚠️'}
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {errorCode === 'expired' ? 'Link abgelaufen' : 'Login fehlgeschlagen'}
              </h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{errorMsg}</p>
              <p className="text-xs text-gray-500 mt-1">
                {errorCode === 'expired'
                  ? 'Magic Links sind 60 Minuten gültig. Bitte fordere einen neuen an.'
                  : 'Der Link ist möglicherweise nicht mehr gültig oder wurde manipuliert.'}
              </p>

              <Link
                to="/admin/login"
                className="inline-block mt-6 px-5 py-2.5 rounded-md text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary/90 transition-colors"
              >
                Zurück zum Login
              </Link>
            </>
          )}
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-6">
          &copy; {new Date().getFullYear()} SeaDent Mallorca · Admin
        </p>
      </div>
    </div>
  );
};

export default MagicLinkVerifyPage;
